// Helpers for the event bus tracking screens (Event.js and the dashboard's
// event tracking modal).
//
// An event booking carries its bus under `category.event_bus_map`. When the
// school swaps the bus for a day, that map also carries `substitute_vehicle`
// entries. Parents must then see the bus that will actually arrive - exactly the
// way the live transport tracking screen shows it - together with the operator
// contact numbers of that bus.

const asArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "object") return [value];
  return [];
};

const clean = (value) => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  if (!str || str === "null" || str === "undefined" || str === "--") return null;
  return str;
};

// Route urls arrive either as a plain string or nested inside a vehicle object.
const pickRouteUrl = (value) => {
  if (!value) return null;
  if (typeof value === "object") return clean(value.vehicle_route_url);
  return clean(value);
};

// A substitute is assigned for "pick", "drop" or "pick & drop". Anything that
// covers both legs (or carries no trip at all) is treated as "both".
export const normalizeTrip = (trip) => {
  const value = String(trip || "").toLowerCase().trim();
  const hasPick = value.includes("pick");
  const hasDrop = value.includes("drop");
  if (hasPick && !hasDrop) return "pick";
  if (hasDrop && !hasPick) return "drop";
  return "both";
};

export const formatTripLabel = (trip) => {
  switch (normalizeTrip(trip)) {
    case "pick":
      return "Pick";
    case "drop":
      return "Drop";
    default:
      return "Pick and Drop";
  }
};

export const toDateKey = (value) => {
  if (!value) return null;
  // Substitute dates are date-only values sent as UTC midnight, so the date
  // part of the ISO string is what the school actually picked.
  const str = String(value);
  const isoMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  const d = new Date(str);
  if (isNaN(d.getTime())) return null;
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

export const todayKey = () => {
  const d = new Date();
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

export const formatSubstituteDate = (value) => {
  const key = toDateKey(value);
  if (!key) return null;
  const [year, month, day] = key.split("-").map(Number);
  const d = new Date(year, month - 1, day);
  if (isNaN(d.getTime())) return key;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * Whether an event still belongs to today. Tracking is open between the event's
 * start and end date, falling back to the application due date when the school
 * did not set an end. An event with no dates at all is treated as open, which
 * matches the tracking window check used by the modals.
 */
export const isEventTrackableToday = (dates) => {
  if (!dates) return false;
  const startStr = dates?.start_date || dates?.category?.start_date;
  const endStr = dates?.end_date || dates?.category?.end_date || dates?.due_date || dates?.category?.due_date;
  
  const startKey = toDateKey(startStr);
  const endKey = toDateKey(endStr);
  const todayStr = todayKey();

  if (startKey && endKey) {
    return todayStr >= startKey && todayStr <= endKey;
  }
  if (startKey) {
    return todayStr === startKey;
  }
  if (endKey) {
    return todayStr === endKey;
  }

  return false;
};

// Event bus maps carry no pick/drop windows, so the leg is derived from the
// clock: mornings belong to the pickup trip, afternoons to the drop trip.
export const getCurrentEventTripPhase = () => (new Date().getHours() < 12 ? "pick" : "drop");

export const getEventBusMap = (booking, event) =>
  booking?.category?.event_bus_map ||
  booking?.event_bus_map ||
  event?.event_bus_map ||
  null;

const contact = (label, name, phone, extra = {}) => {
  const cleanName = clean(name);
  const cleanPhone = clean(phone);
  if (!cleanName && !cleanPhone) return null;
  return { label, name: cleanName, phone: cleanPhone, ...extra };
};

// Operator names/numbers live under different keys depending on the endpoint, so
// every known shape is probed before giving up.
const getOriginalContacts = (busMap) => {
  const vehicle = busMap?.vehicle || busMap?.vehicle_details || {};
  const driverPhone = busMap?.driver_contact_no || vehicle?.driver_contact_no;
  const attendeePhone = busMap?.attendee_contact_no || vehicle?.attendee_contact_no;

  return [
    contact(
      "Pick Driver",
      busMap?.morning_driver_name || busMap?.morningShiftDriver?.staff_full_name,
      busMap?.morning_driver_contact_no || busMap?.morningShiftDriver?.mobile_no || driverPhone
    ),
    contact(
      "Drop Driver",
      busMap?.evening_driver_name || busMap?.eveningShiftDriver?.staff_full_name,
      busMap?.evening_driver_contact_no || busMap?.eveningShiftDriver?.mobile_no || driverPhone
    ),
    contact(
      "Pick Attendee",
      busMap?.morning_attendee_name || busMap?.morningShiftAttendee?.staff_full_name,
      busMap?.morning_attendee_contact_no || busMap?.morningShiftAttendee?.mobile_no || attendeePhone
    ),
    contact(
      "Drop Attendee",
      busMap?.evening_attendee_name || busMap?.eveningShiftAttendee?.staff_full_name,
      busMap?.evening_attendee_contact_no || busMap?.eveningShiftAttendee?.mobile_no || attendeePhone
    ),
    contact("Bus Contact", vehicle?.contact_person, vehicle?.contact_number),
  ].filter(Boolean);
};

const getSubstituteContacts = (raw, subVehicle, busMap) => {
  const details = raw?.substitute_driver_details || raw?.driver_details || {};
  const tripLabel = formatTripLabel(raw?.trip);

  // "Existing operator" means the regular bus staff continue on the substitute
  // bus, so their contacts are the ones the parent needs.
  if (raw?.is_existing_operator === true) {
    return getOriginalContacts(busMap).map((item) => ({
      ...item,
      note: "same as regular bus",
    }));
  }

  const driverName =
    details?.driver_name ||
    details?.staff_full_name ||
    details?.driver?.staff_full_name ||
    details?.morningShiftDriver?.staff_full_name ||
    details?.eveningShiftDriver?.staff_full_name ||
    raw?.substitute_driver_name ||
    raw?.driver_name;

  const driverPhone =
    details?.driver_contact_no ||
    details?.mobile_no ||
    details?.driver?.mobile_no ||
    details?.morningShiftDriver?.mobile_no ||
    details?.eveningShiftDriver?.mobile_no ||
    raw?.substitute_driver_contact_no ||
    raw?.driver_contact_no ||
    subVehicle?.driver_contact_no;

  const attendeeName =
    details?.attendee_name ||
    details?.attendee?.staff_full_name ||
    details?.morningShiftAttendee?.staff_full_name ||
    details?.eveningShiftAttendee?.staff_full_name ||
    raw?.substitute_attendee_name ||
    raw?.attendee_name;

  const attendeePhone =
    details?.attendee_contact_no ||
    details?.attendee?.mobile_no ||
    details?.morningShiftAttendee?.mobile_no ||
    details?.eveningShiftAttendee?.mobile_no ||
    raw?.substitute_attendee_contact_no ||
    raw?.attendee_contact_no ||
    subVehicle?.attendee_contact_no;

  return [
    contact(`Substitute Driver (${tripLabel})`, driverName, driverPhone, { highlight: true }),
    contact(`Substitute Attendee (${tripLabel})`, attendeeName, attendeePhone, { highlight: true }),
    contact("Substitute Bus Contact", subVehicle?.contact_person, subVehicle?.contact_number),
  ].filter(Boolean);
};

const normalizeSubstitute = (raw, busMap) => {
  if (!raw) return null;
  const subVehicle =
    (raw.substitute_vehicle && typeof raw.substitute_vehicle === "object" ? raw.substitute_vehicle : null) ||
    raw.substitute_vehicle_details ||
    raw.vehicle ||
    {};

  return {
    raw,
    trip: normalizeTrip(raw.trip),
    tripLabel: formatTripLabel(raw.trip),
    hasTrip: Boolean(clean(raw.trip)),
    substituteDate: raw.substitute_date || null,
    substituteDateLabel: formatSubstituteDate(raw.substitute_date),
    vehicleNumber:
      clean(subVehicle?.vehicle_number) ||
      clean(raw.substitute_vehicle_number) ||
      clean(raw.vehicle_number),
    trackUrl:
      pickRouteUrl(subVehicle?.vehicle_route_url) ||
      pickRouteUrl(raw.substitute_vehicle_route_url) ||
      pickRouteUrl(raw.vehicle_route_url),
    isExistingOperator: raw.is_existing_operator === true,
    contacts: getSubstituteContacts(raw, subVehicle, busMap),
  };
};

export const getEventSubstituteList = (busMap) => {
  if (!busMap) return [];
  const raw = asArray(busMap.substitute_vehicle).length
    ? asArray(busMap.substitute_vehicle)
    : asArray(busMap.vehicle_substitute);

  return raw
    .filter((item) => item && typeof item === "object" && item.is_deleted !== true)
    .map((item) => normalizeSubstitute(item, busMap))
    .filter(Boolean);
};

// Substitutes that matter right now: assigned for today and, when the school
// assigned a different bus for each leg, the one covering the current leg.
export const getActiveEventSubstitutes = (busMap) => {
  const list = getEventSubstituteList(busMap);
  if (!list.length) return [];

  const today = todayKey();
  const dated = list.filter((sub) => {
    const key = toDateKey(sub.substituteDate);
    // No date at all means the school did not scope it to a single day.
    if (!key) return busMap?.is_substitute_today !== false;
    return key === today;
  });

  return dated;
};

/**
 * Everything the tracking screens need for one event booking: which bus is
 * arriving, where to track it and whom to call.
 */
export const getEventBusTrackingInfo = (booking, event, fallbackUrl) => {
  const busMap = getEventBusMap(booking, event);
  const vehicle = busMap?.vehicle || busMap?.vehicle_details || null;

  const originalVehicleNumber = clean(vehicle?.vehicle_number);
  const originalTrackUrl = pickRouteUrl(vehicle?.vehicle_route_url) || pickRouteUrl(fallbackUrl);

  const activeSubstitutes = getActiveEventSubstitutes(busMap);
  const phase = getCurrentEventTripPhase();
  const substitute =
    activeSubstitutes.length > 1
      ? activeSubstitutes.find((sub) => sub.trip === phase) ||
        activeSubstitutes.find((sub) => sub.trip === "both") ||
        activeSubstitutes[0]
      : activeSubstitutes[0] || null;

  const hasSubstitute = Boolean(substitute);
  const activeTrackUrl = hasSubstitute
    ? substitute.trackUrl || originalTrackUrl
    : originalTrackUrl;

  // Every bus with a live link stays selectable, so a parent can still peek at
  // the regular bus (or the other leg's substitute) if they want to.
  const vehicleOptions = [];
  const pushOption = (label, value) => {
    if (!value || vehicleOptions.some((opt) => opt.value === value)) return;
    vehicleOptions.push({ label, value });
  };

  if (hasSubstitute) {
    pushOption(
      `${substitute.vehicleNumber || "Substitute Bus"} (Substitute${substitute.hasTrip ? ` - ${substitute.tripLabel}` : ""})`,
      substitute.trackUrl
    );
  }
  activeSubstitutes
    .filter((sub) => sub !== substitute)
    .forEach((sub) =>
      pushOption(
        `${sub.vehicleNumber || "Substitute Bus"} (Substitute${sub.hasTrip ? ` - ${sub.tripLabel}` : ""})`,
        sub.trackUrl
      )
    );
  pushOption(`${originalVehicleNumber || "Bus"}${hasSubstitute ? " (Regular Bus)" : ""}`, originalTrackUrl);

  return {
    busMap,
    vehicle,
    hasSubstitute,
    substitute,
    activeSubstitutes,
    originalVehicleNumber,
    originalTrackUrl,
    activeVehicleNumber: hasSubstitute ? substitute.vehicleNumber : originalVehicleNumber,
    activeTrackUrl,
    vehicleOptions,
    contacts: hasSubstitute ? substitute.contacts : getOriginalContacts(busMap),
    originalContacts: getOriginalContacts(busMap),
  };
};
